import { useCallback, useRef, useState } from "react";
import { uploadApis } from "../../../api/Uploads";
import {
  UploadReportState,
  emptyReport,
  parseReport,
  isValidationDone,
  isCreationDone,
  isTerminalFailure,
} from "./uploadReport";

// Polling loop lifted verbatim from the norms upload modal. The browser only
// kicks jobs off and polls — validation and creation run on backend workers,
// so dropping the poll (close / unmount / supersede) loses nothing.
export const POLL_INTERVAL = 1800;
export const MAX_CONSECUTIVE_POLL_ERRORS = 5;
export const MAX_POLLS = 160; // ~5 min ceiling before handing off to Uploads History

export type UploadPollPhase = "validation" | "creation";

export interface UseUploadReportPollOptions {
  /** Defaults to uploadApis.fetchWBSReportById (the shared fetchReportById endpoint). */
  fetchReport?: (reportId: number, pageNo: number, size: number) => Promise<any>;
  /** Page size for the report fetch (first page only). Defaults to 500. */
  pageSize?: number;
  onValidated?: (report: UploadReportState) => void;
  onCreated?: (report: UploadReportState) => void;
  onFailed?: (report: UploadReportState, phase: UploadPollPhase) => void;
}

export interface UseUploadReportPoll {
  report: UploadReportState;
  pollTimedOut: boolean;
  /** Live backend progress ("1500/6202 rows validated") while a phase runs; '' for types that don't write it. */
  progress: string;
  startPolling: (reportId: number, phase: UploadPollPhase) => void;
  /** Kills any in-flight polling loop (generation bump). Backend jobs continue unaffected. */
  stopPolling: () => void;
  /** Stops polling and resets report + pollTimedOut to their empty state. */
  resetReport: () => void;
}

// Stage state deliberately stays OUT of this hook — the caller owns its own
// stage machine (modal, page, drawer…) and reacts via the callbacks.
export const useUploadReportPoll = (
  options: UseUploadReportPollOptions = {}
): UseUploadReportPoll => {
  const [report, setReport] = useState<UploadReportState>(emptyReport);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  // Kept OUTSIDE the report state so mid-poll updates never touch `report`
  // (other upload types' logic depends on report only changing at terminal
  // states). setState with an identical '' bails out — zero re-renders for
  // types that never write progress.
  const [progress, setProgress] = useState("");
  // generation guard: bumping it kills any in-flight polling loop
  const pollGen = useRef(0);

  // Keep options in a ref so the polling loop always sees the latest
  // callbacks without re-creating startPolling (identical closure behavior
  // to the original inline pollReport).
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Once a phase reaches a terminal state, fetch the REMAINING pages of the
  // preview once and merge them in (fire-and-forget, generation-guarded).
  // Polling itself stays cheap (first page only, every tick); this one-time
  // hydration is what lets a 6,000-row file show ALL rows — and, critically,
  // every error/warning row — instead of just the first page. A failure mid-
  // hydration leaves a partial (but truthful) row set; the modal's
  // "Showing X of Y" note covers that case.
  const hydrateRemainingRows = useCallback(
    async (reportId: number, record: UploadReportState, gen: number) => {
      const total = record.totalRecords;
      if (!total || total <= record.rows.length) return;
      const fetchReport =
        optionsRef.current.fetchReport || uploadApis.fetchWBSReportById;
      const pageSize = optionsRef.current.pageSize ?? 500;
      const totalPages = Math.ceil(total / pageSize);
      let allRows = [...record.rows];
      for (let page = 2; page <= totalPages; page++) {
        if (pollGen.current !== gen) return;
        try {
          const res = await fetchReport(reportId, page, pageSize);
          if (pollGen.current !== gen) return;
          const parsed = parseReport(res?.data || res);
          if (!parsed.rows.length) return;
          allRows = allRows.concat(parsed.rows);
          const merged = { ...record, rows: allRows };
          setReport(merged);
        } catch {
          return;
        }
      }
    },
    []
  );

  // Poll the report until the given phase reaches a terminal state
  const startPolling = useCallback(
    async (reportId: number, phase: UploadPollPhase) => {
      const gen = ++pollGen.current;
      let consecutiveErrors = 0;
      for (let attempt = 0; attempt < MAX_POLLS; attempt++) {
        if (pollGen.current !== gen) return; // superseded / caller reset
        try {
          const fetchReport =
            optionsRef.current.fetchReport || uploadApis.fetchWBSReportById;
          const pageSize = optionsRef.current.pageSize ?? 500;
          const res = await fetchReport(reportId, 1, pageSize);
          if (pollGen.current !== gen) return;
          consecutiveErrors = 0;
          const record = parseReport(res?.data || res);
          setProgress(record.progressText);

          if (phase === "validation") {
            if (isValidationDone(record)) {
              setReport(record);
              optionsRef.current.onValidated?.(record);
              void hydrateRemainingRows(reportId, record, gen);
              return;
            }
            if (isTerminalFailure(record)) {
              setReport(record);
              optionsRef.current.onFailed?.(record, phase);
              void hydrateRemainingRows(reportId, record, gen);
              return;
            }
          } else {
            if (isCreationDone(record)) {
              setReport(record);
              optionsRef.current.onCreated?.(record);
              void hydrateRemainingRows(reportId, record, gen);
              return;
            }
            if (isTerminalFailure(record)) {
              setReport(record);
              optionsRef.current.onFailed?.(record, phase);
              void hydrateRemainingRows(reportId, record, gen);
              return;
            }
          }
        } catch (err) {
          consecutiveErrors++;
          if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
            if (pollGen.current !== gen) return;
            setPollTimedOut(true);
            return;
          }
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      }
      if (pollGen.current === gen) setPollTimedOut(true);
    },
    [hydrateRemainingRows]
  );

  const stopPolling = useCallback(() => {
    pollGen.current += 1; // stop polling; backend jobs continue unaffected
  }, []);

  const resetReport = useCallback(() => {
    pollGen.current += 1;
    setReport(emptyReport);
    setPollTimedOut(false);
    setProgress("");
  }, []);

  return { report, pollTimedOut, progress, startPolling, stopPolling, resetReport };
};
